import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import api from "../api";
import FileAttachment from "../components/FileAttachment";
import DecimalInput from "../components/DecimalInput";
import IntegerInput from "../components/IntegerInput";
import MaskedInput from "../components/MaskedInput";
import { normalizeStoredUrl, resolveAssetUrl } from "../utils/assets";
import {
  getMaxUploadFileSizeMessage,
  getUploadLimitExceededMessage,
  isFileWithinUploadLimit,
  MAX_UPLOAD_FILE_SIZE_BYTES,
  MAX_UPLOAD_FILE_SIZE_MB,
} from "../utils/uploadLimits";
import { applyAttachmentTemplate } from "../utils/attachmentNaming";
import TableField from "../components/TableField";


const getAutoFillValue = (source) => {
  if (!source) return "";
  const today = new Date().toISOString().slice(0, 10);
  if (source === "today_date") return today;
  return "";
};

const applyAutoFillDefaults = (fields = [], values = {}) => {
  const nextValues = { ...values };
  fields.forEach((field) => {
    if (!field?.id || !field.autoFillSource) return;
    const currentValue = nextValues[field.id];
    const isEmpty = currentValue === undefined || currentValue === null || currentValue === "";
    if (!isEmpty) return;
    const prefilled = getAutoFillValue(field.autoFillSource);
    if (prefilled !== "") nextValues[field.id] = prefilled;
  });
  return nextValues;
};

function fieldVisible(field, values) {
  if (!field.visibleWhen) return true;
  try {
    const fn = new Function("values", `with (values) { return (${field.visibleWhen}); }`);
    return !!fn(values);
  } catch (error) {
    console.error("Erro ao avaliar condição de visibilidade", error);
    return false;
  }
}

function buildShareMessage(process, form) {
  if (process?.name) return process.name;
  return form?.name || "Formulário";
}

const formatTaxId = (value) => {
  if (!value) return null;
  const digits = `${value}`.replace(/\D/g, "");
  if (digits.length === 14) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
  }
  if (digits.length === 11) {
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
  }
  return value;
};

const normalizeWebsite = (value) => {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const getWebsiteLabel = (value) => {
  if (!value) return "";
  try {
    const url = new URL(normalizeWebsite(value));
    return url.hostname.replace(/^www\./i, "");
  } catch {
    return value.replace(/^https?:\/\//i, "");
  }
};

const stripDigits = (value) => `${value || ""}`.replace(/\D/g, "");

const isValidCPF = (value) => {
  const digits = stripDigits(value);
  if (digits.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(digits)) return false;

  const calcDigit = (base) => {
    let sum = 0;
    for (let i = 0; i < base.length; i += 1) {
      sum += parseInt(base[i], 10) * (base.length + 1 - i);
    }
    const remainder = sum % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  };

  const firstDigit = calcDigit(digits.slice(0, 9));
  if (firstDigit !== parseInt(digits[9], 10)) return false;

  const secondDigit = calcDigit(digits.slice(0, 10));
  return secondDigit === parseInt(digits[10], 10);
};

const isValidCNPJ = (value) => {
  const digits = stripDigits(value);
  if (digits.length !== 14) return false;
  if (/^(\\d)\\1{13}$/.test(digits)) return false;

  const calcDigit = (base, weights) => {
    let sum = 0;
    for (let i = 0; i < base.length; i += 1) {
      sum += parseInt(base[i], 10) * weights[i];
    }
    const remainder = sum % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  };

  const firstWeights = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const secondWeights = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

  const firstDigit = calcDigit(digits.slice(0, 12), firstWeights);
  if (firstDigit !== parseInt(digits[12], 10)) return false;

  const secondDigit = calcDigit(digits.slice(0, 13), secondWeights);
  return secondDigit === parseInt(digits[13], 10);
};

const isValidEmail = (value) => {
  if (!value) return true;
  const email = String(value).trim();
  if (!email) return true;
  const pattern = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
  return pattern.test(email);
};

const fieldSizeClass = {
  1: "md:col-span-1",
  2: "md:col-span-2",
  3: "md:col-span-3",
  4: "md:col-span-4",
  5: "md:col-span-5",
  6: "md:col-span-6",
  7: "md:col-span-7",
  8: "md:col-span-8",
  9: "md:col-span-9",
  10: "md:col-span-10",
  11: "md:col-span-11",
  12: "md:col-span-12",
};

const getFieldLayoutClass = (size) => `col-span-1 ${fieldSizeClass[size] || fieldSizeClass[12]}`;

export default function ExternalForm() {
  const { token } = useParams();
  const location = useLocation();
  const isExternalStepTask = location.pathname.includes("/tarefas-externas/");
  const [form, setForm] = useState(null);
  const [processInfo, setProcessInfo] = useState(null);
  const [company, setCompany] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const companyLogoUrl = useMemo(() => resolveAssetUrl(company?.logoUrl), [company?.logoUrl]);

  useEffect(() => {
    setLoading(true);
    setLoadError("");
    setSubmitError("");
    setSuccess(null);
    setValues({});
    setCompany(null);
    const endpoint = isExternalStepTask ? `/public/step-tasks/${token}` : `/public/forms/${token}`;
    api
      .get(endpoint)
      .then((res) => {
        setForm(res.data.form);
        setProcessInfo(res.data.process);
        const initialValues = (res.data.formData && typeof res.data.formData === "object") ? res.data.formData : {};
        const schemaFields = Array.isArray(res.data.form?.schema?.fields) ? res.data.form.schema.fields : [];
        setValues(applyAutoFillDefaults(schemaFields, initialValues));
        const companyData = res.data.company;
        if (companyData) {
          setCompany({
            ...companyData,
            logoUrl: normalizeStoredUrl(companyData.logoUrl || companyData.logo),
          });
        } else {
          setCompany(null);
        }
      })
      .catch((err) => {
        const msg = err.response?.data?.error || (isExternalStepTask ? "Tarefa externa indisponível" : "Formulário indisponível");
        setLoadError(msg);
      })
      .finally(() => setLoading(false));
  }, [token, isExternalStepTask]);

  const handleChange = (event) => {
    const { name, value, options, multiple } = event.target;
    const nextValue = multiple
      ? Array.from(options)
          .filter((option) => option.selected)
          .map((option) => option.value)
      : value;

    setValues((prev) => ({ ...prev, [name]: nextValue }));
  };

  const handleFileChange = async (name, file) => {
    if (!file) return;
    if (!isFileWithinUploadLimit(file)) {
      alert(getMaxUploadFileSizeMessage());
      return;
    }
    const field = form?.schema?.fields?.find((f) => f.id === name);
    const schemaFields = Array.isArray(form?.schema?.fields) ? form.schema.fields : [];
    let selectedFile = file;
    let generatedFileName = null;
    if (field?.fileNameTemplate) {
      const { file: renamedFile, generatedName } = applyAttachmentTemplate(file, field.fileNameTemplate, {
        values,
        fields: schemaFields,
        originalFileName: file.name,
      });
      selectedFile = renamedFile;
      generatedFileName = generatedName;
    }
    const data = new FormData();
    data.append("file", selectedFile);
    const category =
      processInfo?.category || processInfo?.name || form?.name || "processo-externo";
    const formName = form?.name || processInfo?.name || "Formulário";
    const fieldTitle = field?.label || name;
    data.append("category", category);
    data.append("formName", formName);
    data.append("fieldTitle", fieldTitle);
    data.append("insertedAt", new Date().toISOString());
    if (generatedFileName) {
      data.append("generatedFileName", generatedFileName);
    }
    try {
      const res = await api.post("/upload", data);
      setValues((prev) => ({
        ...prev,
        [name]: {
          url: res.data.url,
          downloadUrl: res.data.downloadUrl,
          downloadPath: res.data.downloadPath,
          relativePath: res.data.relativePath,
          name: res.data.name,
          metadata: res.data.metadata,
        },
      }));
    } catch (err) {
      const msg =
        err?.response?.status === 413
          ? getUploadLimitExceededMessage()
          : err.response?.data?.error || "Falha ao enviar arquivo";
      alert(msg);
    }
  };

  const fields = useMemo(
    () => (Array.isArray(form?.schema?.fields) ? form.schema.fields.filter((field) => field?.type !== "linkedField") : []),
    [form]
  );
  const groups = useMemo(() => form?.schema?.groups || [], [form]);
  const showCompanyHeader = form?.schema?.settings?.showCompanyHeader !== false;

  const visibleGroupedFields = useMemo(() => {
    const grouped = new Map();
    for (const f of fields) {
      const gid = f.groupId || "__ungrouped__";
      if (!grouped.has(gid)) grouped.set(gid, []);
      if (fieldVisible(f, values)) {
        grouped.get(gid).push(f);
      }
    }
    return grouped;
  }, [fields, values]);

  const handleTableFileChange = async (field, rowIndex, column, file) => {
    if (!file) return;
    if (!isFileWithinUploadLimit(file)) {
      alert(getMaxUploadFileSizeMessage());
      return;
    }

    const data = new FormData();
    data.append("file", file);
    const category = processInfo?.category || processInfo?.name || form?.name || "processo-externo";
    const formName = form?.name || processInfo?.name || "Formulário";
    data.append("category", category);
    data.append("formName", formName);
    data.append("fieldTitle", `${field?.label || field?.id} - ${column?.label || column?.id}`);
    data.append("insertedAt", new Date().toISOString());

    try {
      const response = await api.post("/upload", data);
      const fileInfo = {
        url: response.data.url,
        downloadUrl: response.data.downloadUrl,
        downloadPath: response.data.downloadPath,
        relativePath: response.data.relativePath,
        name: response.data.name,
        metadata: response.data.metadata,
      };
      setValues((prev) => {
        const rows = Array.isArray(prev[field.id]) ? [...prev[field.id]] : [];
        const row = rows[rowIndex] && typeof rows[rowIndex] === "object" ? rows[rowIndex] : {};
        rows[rowIndex] = {
          ...row,
          [column.id]: fileInfo,
        };
        return { ...prev, [field.id]: rows };
      });
    } catch (error) {
      const message =
        error?.response?.status === 413
          ? getUploadLimitExceededMessage()
          : error?.response?.data?.error || error?.message || "Erro ao enviar arquivo";
      alert(message);
    }
  };

  const renderField = (field) => {
    const commonProps = {
      id: field.id,
      name: field.id,
      value: values[field.id] || "",
      onChange: handleChange,
      required: field.required,
      className:
        "w-full rounded-lg border-2 border-blue-200 bg-blue-50/80 px-3 py-2 text-sm text-slate-800 placeholder:text-slate-500 shadow focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-200",
      readOnly: Boolean(field.autoFillSource) && field.autoFillEditable === false,
    };
    const inputClassName = commonProps.className;

    if (field.type === "table") {
      return (
        <TableField
          field={field}
          value={values[field.id]}
          onChange={(nextRows) => setValues((prev) => ({ ...prev, [field.id]: nextRows }))}
          inputClassName={inputClassName}
          onUploadFile={({ rowIndex, column, file }) => handleTableFileChange(field, rowIndex, column, file)}
        />
      );
    }

    if (field.type === "list") {
      const selectValue = field.allowMultiple
        ? Array.isArray(values[field.id])
          ? values[field.id]
          : values[field.id]
            ? [values[field.id]]
            : []
        : values[field.id] || "";

      return (
        <select {...commonProps} disabled={commonProps.readOnly} multiple={field.allowMultiple} value={selectValue}>
          {!field.allowMultiple && (
            <option value="" disabled>
              Selecione...
            </option>
          )}
          {(field.options || []).map((option, idx) => (
            <option key={idx} value={option}>
              {option}
            </option>
          ))}
        </select>
      );
    }
    if (field.type === "file") {
      const current = values[field.id];
      const hint = `Tamanho máximo do arquivo: ${MAX_UPLOAD_FILE_SIZE_MB} MB.`;
      return current ? (
        <div className="space-y-2">
          <FileAttachment file={current} />
          <p className="text-xs text-slate-500">{hint}</p>
        </div>
      ) : (
        <div className="space-y-2">
          <input
            type="file"
            onChange={(event) => {
              const selectedFile = event.target.files?.[0];
              if (!selectedFile) return;
              if (selectedFile.size > MAX_UPLOAD_FILE_SIZE_BYTES) {
                alert(getMaxUploadFileSizeMessage());
                event.target.value = "";
                return;
              }
              handleFileChange(field.id, selectedFile);
            }}
            required={field.required}
            className="w-full rounded-lg border-2 border-dashed border-blue-200 bg-blue-50/80 px-3 py-2 text-sm text-slate-800 shadow focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
          <p className="text-xs text-slate-500">{hint}</p>
        </div>
      );
    }
    if (field.type === "decimal") {
      return (
        <DecimalInput
          {...commonProps}
          decimalPlaces={field.decimalPlaces || 2}
          placeholder={field.placeholder || ""}
        />
      );
    }
    if (field.type === "number") {
      return (
        <IntegerInput
          {...commonProps}
          placeholder={field.placeholder || ""}
        />
      );
    }
    if (field.type === "phone") {
      return (
        <MaskedInput
          {...commonProps}
          mask="phone"
          placeholder={field.placeholder || ""}
        />
      );
    }
    if (field.type === "cpf") {
      return (
        <MaskedInput
          {...commonProps}
          mask="cpf"
          placeholder={field.placeholder || ""}
        />
      );
    }
    if (field.type === "cnpj") {
      return (
        <MaskedInput
          {...commonProps}
          mask="cnpj"
          placeholder={field.placeholder || ""}
        />
      );
    }
    if (field.type === "cep") {
      return (
        <MaskedInput
          {...commonProps}
          mask="cep"
          placeholder={field.placeholder || ""}
        />
      );
    }
    if (field.type === "email") {
      return (
        <input
          {...commonProps}
          type="email"
          inputMode="email"
          autoComplete="email"
          pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
          placeholder={field.placeholder || ""}
        />
      );
    }
    if (field.type === "date") {
      return (
        <input
          {...commonProps}
          type="date"
          onFocus={(event) => event.target.showPicker?.()}
          placeholder={field.placeholder || ""}
        />
      );
    }
    if (field.type === "time") {
      return (
        <input
          {...commonProps}
          type="time"
          step={field.step || 60}
          placeholder={field.placeholder || ""}
        />
      );
    }
    if (field.type === "textarea") {
      const textareaRows = Math.max(3, Number(field.rows) || 0);
      return (
        <textarea
          {...commonProps}
          rows={textareaRows}
          placeholder={field.placeholder || ""}
          className={`${inputClassName} resize-y`}
        />
      );
    }
    return (
      <input
        {...commonProps}
        type="text"
        placeholder={field.placeholder || ""}
      />
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form) return;

    const requiredFields = fields.filter(
      (f) => f.required && fieldVisible(f, values)
    );
    for (const field of requiredFields) {
      const value = values[field.id];
      if (field.type === "table") {
        const rows = Array.isArray(value) ? value : [];
        if (rows.length === 0) {
          alert(`Adicione ao menos uma linha no campo "${field.label}".`);
          return;
        }
        const requiredColumns = Array.isArray(field.tableColumns)
          ? field.tableColumns.filter((column) => column.required)
          : [];
        for (const column of requiredColumns) {
          const hasMissing = rows.some((row) => {
            const cell = row?.[column.id];
            return cell === undefined || cell === null || cell === "";
          });
          if (hasMissing) {
            alert(`Preencha a coluna obrigatória "${column.label}" em "${field.label}".`);
            return;
          }
        }
        continue;
      }
      const isEmptyList = Array.isArray(value) && value.length === 0;
      if (value === undefined || value === "" || isEmptyList) {
        alert(`O campo "${field.label}" é obrigatório.`);
        return;
      }
    }

    for (const field of fields) {
      if (!fieldVisible(field, values)) continue;
      const value = values[field.id];
      if (!value) continue;
      if (field.type === "cpf" && !isValidCPF(value)) {
        alert(`Informe um CPF válido no campo "${field.label}".`);
        return;
      }
      if (field.type === "cnpj" && !isValidCNPJ(value)) {
        alert(`Informe um CNPJ válido no campo "${field.label}".`);
        return;
      }
      if (field.type === "email" && !isValidEmail(value)) {
        alert(`Informe um e-mail válido no campo "${field.label}".`);
        return;
      }
    }

    setSubmitting(true);
    setSubmitError("");
    try {
      const submitEndpoint = isExternalStepTask ? `/public/step-tasks/${token}/submit` : `/public/forms/${token}/submit`;
      const res = await api.post(submitEndpoint, values);
      setSuccess({
        instanceId: res.data.instanceId,
        done: res.data.done,
      });
      setValues({});
    } catch (err) {
      const msg = err.response?.data?.error || "Não foi possível enviar sua solicitação.";
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const title = buildShareMessage(processInfo, form);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 px-4 py-10">
      <div className="mx-auto w-full max-w-4xl rounded-3xl bg-white/90 p-6 shadow-xl ring-1 ring-slate-100 backdrop-blur">
        {loading ? (
          <div className="py-20 text-center text-slate-500">Carregando formulário...</div>
        ) : loadError ? (
          <div className="py-20 text-center">
            <h1 className="text-2xl font-semibold text-slate-800">Não foi possível exibir o formulário</h1>
            <p className="mt-3 text-slate-600">{loadError}</p>
          </div>
        ) : success ? (
          <div className="py-20 text-center">
            <h1 className="text-3xl font-semibold text-green-600">Solicitação enviada!</h1>
            <p className="mt-4 text-slate-600">
              Anote o número da sua solicitação para acompanhar com a equipe:
            </p>
            <p className="mt-6 text-4xl font-bold text-slate-900">#{success.instanceId}</p>
            <button
              onClick={() => {
                setSuccess(null);
                setValues({});
              }}
              className="mt-10 rounded-full bg-blue-600 px-6 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700"
            >
              Enviar nova solicitação
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-8">
            {company && showCompanyHeader && (
              <section className="flex flex-col gap-4 rounded-2xl border border-blue-100 bg-blue-50/70 p-4 shadow-sm sm:flex-row sm:items-center">
                {companyLogoUrl && (
                  <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-xl bg-white shadow-inner">
                    <img
                      src={companyLogoUrl}
                      alt={company.tradeName || company.name}
                      className="max-h-14 w-full object-contain"
                    />
                  </div>
                )}
                <div className="flex-1 space-y-2 text-sm">
                  <div>
                    <p className="text-base font-semibold text-blue-900">
                      {company.tradeName || company.name}
                    </p>
                    {company.tradeName && company.name && company.tradeName.trim() !== company.name.trim() && (
                      <p className="text-xs text-blue-700/80">{company.name}</p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-blue-700">
                    {company.taxId && (
                      <span className="font-medium">
                        CNPJ: {formatTaxId(company.taxId)}
                      </span>
                    )}
                    {company.website && (
                      <a
                        href={normalizeWebsite(company.website)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 font-medium text-blue-700 hover:underline"
                      >
                        {getWebsiteLabel(company.website)}
                      </a>
                    )}
                  </div>
                </div>
              </section>
            )}
            <header className="rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white shadow-lg">
              <p className="text-sm uppercase tracking-[0.3em] text-blue-100">
                Formulário externo
              </p>
              <h1 className="mt-2 text-3xl font-semibold">{title}</h1>
              {processInfo?.description && (
                <p className="mt-3 max-w-3xl text-blue-50">
                  {processInfo.description}
                </p>
              )}
            </header>

            {submitError && (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 shadow-sm">
                {submitError}
              </div>
            )}

            {groups.map((group) => {
              const list = visibleGroupedFields.get(group.id) || [];
              if (list.length === 0) return null;
              return (
                <section
                  key={group.id}
                  className="rounded-2xl border border-slate-100 bg-slate-50/70 p-6 shadow-sm"
                >
                  <h2 className="text-lg font-semibold text-slate-800">{group.label}</h2>
                  <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-12">
                    {list.map((field) => (
                      <div
                        key={field.id}
                        className={`flex h-full flex-col gap-3 ${getFieldLayoutClass(field.size)}`}
                      >
                        <div className="space-y-1">
                          <label className="text-sm font-medium text-slate-700">
                            {field.label}
                            {field.required && <span className="text-red-600"> *</span>}
                          </label>
                          {field.description && (
                            <p className="text-xs leading-relaxed text-slate-500">
                              {field.description}
                            </p>
                          )}
                        </div>
                        {renderField(field)}
                      </div>
                    ))}
                  </div>
                </section>
              );
            })}

            {visibleGroupedFields.get("__ungrouped__")?.length ? (
              <section className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
                {groups.length > 0 && (
                  <h2 className="text-lg font-semibold text-slate-800">Campos adicionais</h2>
                )}
                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-12">
                  {visibleGroupedFields.get("__ungrouped__").map((field) => (
                    <div
                      key={field.id}
                      className={`flex h-full flex-col gap-3 ${getFieldLayoutClass(field.size)}`}
                    >
                      <div className="space-y-1">
                        <label className="text-sm font-medium text-slate-700">
                          {field.label}
                          {field.required && <span className="text-red-600"> *</span>}
                        </label>
                        {field.description && (
                          <p className="text-xs leading-relaxed text-slate-500">
                            {field.description}
                          </p>
                        )}
                      </div>
                      {renderField(field)}
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

            <div className="flex items-center justify-end">
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center justify-center rounded-full bg-blue-600 px-6 py-2 text-sm font-semibold text-white shadow transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {submitting ? "Enviando..." : "Enviar solicitação"}
              </button>
            </div>
          </form>
        )}
        {!loading && (
          <footer className="mt-10 flex flex-col items-center gap-4 rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-4 text-center text-xs text-slate-500 sm:flex-row sm:justify-center sm:text-left sm:text-sm">
            <img
              src="/nashlogoazul.png"
              alt="Nash Tecnologia"
              className="h-12 w-auto"
            />
            <p className="max-w-xl">
              Formulário gerado pelo sistema Intelligent BPM Nash Tecnologia. Se quiser saber mais{' '}
              <a
                href="https://www.nashtec.com.br"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-blue-600 hover:underline"
              >
                clique aqui
              </a>{' '}
              ou acesse{' '}
              <a
                href="https://www.nashtec.com.br"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-blue-600 hover:underline"
              >
                www.nashtec.com.br
              </a>
              .
            </p>
          </footer>
        )}
      </div>
    </div>
  );
}
